import { useAtomic } from '@/use/useAtomic';
import { useBrandConfig } from '@/stores/useBrandConfig';
import { useBotUser } from '@/stores/useBotUser';
import { useConfig } from '@/stores/useConfig';
import { useMockedFlowStore, useMockedFlow } from './mocked-flow';

import type { MessageUtilities } from '../message/useMessageUtilities';
import { useMessageUtilities } from '../message/useMessageUtilities';
import { randomId } from '../message/lib';
import { ExternalBotEvent } from '../message/enum';
import { buildCategoryListMessageFromBrandConfig } from './categoryBrowsing.utils';
import type { ResumeAIChatEvent, StartKYCFlowEvent, StartSelectedEntityFlowEvent } from '../message/types';

import { useFlowEventHandlers } from './useFlowEventHandlers';
import { useResumeAiChat } from './ai-service/useResumeAiChat';
import { usePredictiveSuggestions } from './usePredictiveSuggestions';
import { useOpenChatsQuery } from './useOpenChatsQuery';
import { getAiHardcodedConnectConfig } from './ai-service/useAiFlowEventHandlers.utils';
import { storeToRefs } from 'pinia';
import { createEventHook } from '@vueuse/core';
import * as env from '@/common/environment';
import { isLandingEntrySupported, resolveLandingEntryUserId } from '@/common/landingEntry.utils';
import { Country } from '@/common/config';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import { devLog } from '@/common/debug';
import {
  addBreadcrumb,
  captureEventException,
  ErrorTrackingFlow,
  ErrorTrackingEvent,
  setChatbotContext,
} from '@/common/error-tracking';

export function useChatbotFlow(factory?: MessageUtilities) {
  const conversation = factory || useMessageUtilities();

  const resumeAiChatHook = createEventHook<void>();
  const selectedEntityHook = createEventHook<void>();
  const failedKycHook = createEventHook<void>();
  const mockedFlowHook = createEventHook<void>();
  const landingFlowHook = createEventHook<void>();

  const brandConfigStore = useBrandConfig();
  const { fetchBrandConfig } = brandConfigStore;
  const { isSkipToLiveAgentActive } = storeToRefs(brandConfigStore);

  const botUser = useBotUser();
  const { isAuthenticated, user } = storeToRefs(botUser);

  const configStore = useConfig();
  const { config } = storeToRefs(configStore);

  const openChatsQuery = useOpenChatsQuery();
  const resumeAiChat = useResumeAiChat();
  const predictiveSuggestions = usePredictiveSuggestions();
  const mockedFlow = useMockedFlow();

  const mockedFlowStore = useMockedFlowStore();
  const {
    isActive: isMockedFlowActive,
    shouldSkipDefaultFlow: isMockedFlowShouldSkipDefaultFlow,
    messages: mockedFlowMessages,
  } = storeToRefs(mockedFlowStore);

  const applyFlowContext = (flow: ErrorTrackingFlow): void => {
    setChatbotContext({
      flow,
      isGuest: !isAuthenticated.value,
      brand: env.get('brandCode') || undefined,
    });
    addBreadcrumb({
      category: 'chatbot.flow',
      message: flow,
      level: 'info',
      data: { flow, isGuest: !isAuthenticated.value },
    });
  };

  const [start, startFlowRunning] = useAtomic(async () => {
    try {
      setChatbotContext({
        isGuest: !isAuthenticated.value,
        brand: env.get('brandCode') || undefined,
      });
      addBreadcrumb({
        category: 'chatbot.flow',
        message: 'start',
        level: 'info',
        data: { isGuest: !isAuthenticated.value },
      });
      await runStartFlow();
    } catch (error) {
      captureEventException(error, ErrorTrackingEvent.ChatbotFlowStart);
      throw error;
    }
  });

  const runStartFlow = async () => {
    devLog.debug('[CHATBOT_FLOW] Starting chatbot flow');

    if (conversation.messages.value?.length) {
      devLog.debug('[CHATBOT_FLOW] Conversation messages already exist, skipping flow');
      return;
    }

    // PRIORITY 0: Check for mocked flow (highest priority)
    // Mocked flow UI actions are executed in App.vue initProject, here we inject mocked messages
    // Note: We check the store directly (isActive) instead of computed refs from composable
    // because the composable might be instantiated before the store is populated
    if (isMockedFlowActive.value && isMockedFlowShouldSkipDefaultFlow.value) {
      devLog.debug('[CHATBOT_FLOW] Mocked flow enabled with skipDefaultFlow', {
        hasMessages: Boolean(mockedFlowMessages.value.length),
      });
      applyFlowContext(ErrorTrackingFlow.Mocked);
      // Inject mocked messages into the conversation
      if (mockedFlowStore.hasMessages) {
        const mockedMessages = mockedFlowStore.messages;
        conversation.addItems(...mockedMessages);
        // Clear messages after injection to prevent re-injection
        mockedFlowStore.clearMessages();
      }

      mockedFlowHook.trigger();
      return;
    }

    devLog.debug('[CHATBOT_FLOW] Mocked flow not active, checking for other flows');
    // Types issue inside conversation itself – in reality it does support a list
    // @ts-ignore
    await conversation.enqueue(async () => {
      // Fetch all initial data in parallel: open chats (live chat), AI resume, and brand config
      const [openChat, resumeConversation, brandConfig] = await Promise.all([
        isAuthenticated.value ? openChatsQuery.fetchAndCheck() : Promise.resolve(null),
        isAuthenticated.value ? resumeAiChat.fetchAndCheck() : Promise.resolve(null),
        fetchBrandConfig(),
      ]);

      const categoryMessage = buildCategoryListMessageFromBrandConfig(brandConfig);

      // PRIORITY 1: Check if there's a live chat to resume (highest priority)
      // Do nothing, as it will be handled
      if (openChat) {
        applyFlowContext(ErrorTrackingFlow.OpenLiveChat);
        return null;
      }

      // Skip to live agent: bypass categories/AI/predictive. Wizard starts only from
      // openLandingOverlay (input click / New Conversation) — not on init.
      if (isSkipToLiveAgentActive.value) {
        devLog.debug('[CHATBOT_FLOW] Skip to live agent active — waiting for overlay open');
        return null;
      }

      // PRIORITY 2: Failed KYC flow (host signals it via initialize options)
      const failedKyc = config.value?.failedKyc;
      const isFailedKycFlow =
        Boolean(failedKyc?.show && failedKyc?.userRequest) && isAuthenticated.value && checkCanConnectToAI();
      if (isFailedKycFlow) {
        const event: StartKYCFlowEvent = {
          type: ExternalBotEvent.START_KYC_FLOW,
          id: randomId(),
          payload: {
            failedKycMessage: failedKyc!.userRequest,
            supportOptions: getAiHardcodedConnectConfig(),
          },
        };

        devLog.debug('[CHATBOT_FLOW] Starting failed KYC flow', { failedKyc });
        applyFlowContext(ErrorTrackingFlow.FailedKyc);
        submitExternalAction(event);
        failedKycHook.trigger();
        configStore.setConfig({ failedKyc: undefined });

        return categoryMessage;
      }

      // PRIORITY 3: Check for selected entity flow (before AI resume)
      // Only trigger if user is authenticated and no live chat or AI chat to resume
      const selectedEntity = config.value?.selectedEntity;
      const isSelectedEntityFlow = selectedEntity && isAuthenticated.value && checkCanConnectToAI();
      if (isSelectedEntityFlow) {
        // TODO: We need to have a proper solution to assign auto-category for selected entity flow
        // Currently, we'll hardcode category id per type of selected entity
        if (selectedEntity?.type) {
          const categoryId =
            env.get('selectedEntityCategory')?.[selectedEntity?.type]?.[config.value?.country as Country];
          categoryId && $pr.selectedCategory(categoryId);
        }

        const event: StartSelectedEntityFlowEvent = {
          type: ExternalBotEvent.START_SELECTED_ENTITY_FLOW,
          id: randomId(),
          payload: {
            selectedEntity,
            supportOptions: getAiHardcodedConnectConfig(),
          },
        };

        devLog.debug('[CHATBOT_FLOW] Starting selected entity flow', { selectedEntity });
        applyFlowContext(ErrorTrackingFlow.SelectedEntity);
        // Submit the selected entity flow event instead of showing categories
        submitExternalAction(event);
        selectedEntityHook.trigger();
        // Reset selected entity for the next session
        configStore.setConfig({ selectedEntity: undefined });

        return categoryMessage;
      }

      // PRIORITY 4: If we should resume an AI conversation, do that instead of showing categories
      if (resumeConversation && resumeAiChat.shouldResumeConversation.value) {
        const event: ResumeAIChatEvent = {
          type: ExternalBotEvent.RESUME_AI_CHAT,
          id: randomId(),
          payload: resumeConversation,
        };

        applyFlowContext(ErrorTrackingFlow.ResumeAi);
        // Submit the resume AI chat event and return category message
        submitExternalAction(event);
        resumeAiChatHook.trigger();
        return null;
      }

      // PRIORITY 5: Check for predictive suggestions before showing categories
      const predictiveOptions = await predictiveSuggestions.resolvePredictiveOptions(
        isAuthenticated.value ? brandConfig.predictiveSuggestions : null
      );

      // If we have predictive suggestions, show them instead of categories
      if (predictiveOptions.messages.length > 0) {
        devLog.debug('[CHATBOT_FLOW] Showing predictive suggestions');
        applyFlowContext(ErrorTrackingFlow.PredictiveSuggestions);

        return predictiveOptions.messages;
      }

      // PRIORITY 6: Fresh session — landing page depending on env
      if (
        isLandingEntrySupported({
          userId: resolveLandingEntryUserId(isAuthenticated.value, user.value.id),
        })
      ) {
        applyFlowContext(ErrorTrackingFlow.Landing);
        landingFlowHook.trigger();
        return null;
      }

      // PRIORITY 7: Continue with normal flow - show categories
      applyFlowContext(ErrorTrackingFlow.CategoryBrowsing);
      return categoryMessage;
    });
  };

  const {
    submitAction,
    submitExternalAction,
    onConnectSupport,
    onEndChat,
    onTransactionSelected,
    onEditSelectedTransaction,
    flowStatus,
    resetFlow,
    onRestartChat,
    onAbortCancelRequest,
    onConnectOptionSelected,
    onEndLiveChat,
    onEndVoiceCall,
    onResumeChat,
    onVoiceAiUnavailable,

    enterLandingCollapsedState,
    showLandingChannelPicker,
    enterSkipToLiveAgentFlow,
    startLandingAiVoiceCall,
    onLandingAiVoiceStart,

    // AI Flow
    aiFlow,
    checkCanConnectToAI,
    submitUserMessageForAI,
    onAiIntervention,
    onAiFlowStart,
    onEndAiChat,
  } = useFlowEventHandlers({
    conversation,
  });

  return {
    conversation,
    startFlow: start,
    startFlowRunning,
    resetFlow,

    submitFlowAction: submitAction,
    submitFlowExternalAction: submitExternalAction,

    enterLandingCollapsedState,
    showLandingChannelPicker,
    enterSkipToLiveAgentFlow,
    onLandingFlow: landingFlowHook.on,
    startLandingAiVoiceCall,
    onLandingAiVoiceStart,

    onEndChat,
    onRestartChat,
    onAbortCancelRequest,

    onConnectSupport,
    onTransactionSelected,
    onEditSelectedTransaction,
    onConnectOptionSelected,

    onEndLiveChat,
    onEndVoiceCall,
    onResumeChat,
    onVoiceAiUnavailable,
    onSelectedEntityFlow: selectedEntityHook.on,
    onFailedKycFlow: failedKycHook.on,
    flowStatus,

    // AI Flow
    aiFlow,
    checkCanConnectToAI,
    submitUserMessageForAI,
    onAiIntervention,
    onAiFlowStart,
    onEndAiChat,
    onResumeAiChat: resumeAiChatHook.on,

    // Mocked Flow
    mockedFlow,
    onMockedFlow: mockedFlowHook.on,
  };
}
